import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getPaddle } from "@/lib/paddle";
import {
  Film, Sparkles, Upload, Coins, Plus, Trash2, Play, Loader2,
  AlertTriangle, Clock, CheckCircle2, X,
} from "lucide-react";

const GOAL_OPTIONS = [
  { v: "viral_short", label: "Viral short-form" },
  { v: "educational", label: "Educational" },
  { v: "story", label: "Story-driven" },
  { v: "podcast_highlight", label: "Podcast highlight" },
  { v: "promotional", label: "Promotional" },
  { v: "motivational", label: "Motivational" },
  { v: "product", label: "Product / service" },
];

const PLATFORM_OPTIONS = [
  { v: "tiktok", label: "TikTok" },
  { v: "reels", label: "Instagram Reels" },
  { v: "shorts", label: "YouTube Shorts" },
  { v: "linkedin", label: "LinkedIn" },
  { v: "x", label: "X" },
  { v: "custom", label: "Custom" },
];

function statusChip(s) {
  const map = {
    awaiting_upload: { text: "Awaiting upload", color: "text-muted-foreground" },
    uploaded: { text: "Uploaded", color: "text-blue-400" },
    transcribing: { text: "Transcribing", color: "text-yellow-400" },
    transcribed: { text: "Transcribed", color: "text-yellow-400" },
    finding_highlights: { text: "Finding highlights", color: "text-yellow-400" },
    ready_to_edit: { text: "Ready", color: "text-emerald-400" },
    rendering: { text: "Rendering", color: "text-primary" },
    exported: { text: "Exported", color: "text-emerald-400" },
    error: { text: "Error", color: "text-red-400" },
  };
  return map[s] || { text: s, color: "text-muted-foreground" };
}

export default function Studio() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/api/video/projects"),
        api.get("/api/video/credits"),
      ]);
      setProjects(p.data);
      setCredits(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Studio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when returning from Paddle checkout
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("credits_added") === "1") {
      const t = setInterval(async () => { await load(); }, 2000);
      // Stop polling after 20s
      const stopper = setTimeout(() => clearInterval(t), 20000);
      return () => { clearInterval(t); clearTimeout(stopper); };
    }
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this project and all its renders?")) return;
    try {
      await api.delete(`/api/video/projects/${id}`);
      setProjects((ps) => ps.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <header className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-2">
            <Film className="h-3.5 w-3.5" /> ViralCut AI · Studio
          </div>
          <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]" data-testid="studio-heading">
            Turn one long video into <span className="text-primary">3 viral clips.</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Upload a talking-head, podcast, or webinar. ViralCut transcribes it, finds the strongest 20–60 second hooks,
            and exports vertical clips ready for TikTok, Reels, and Shorts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded border border-border bg-card px-4 py-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
              <Coins className="h-3 w-3" /> Credits
            </div>
            <div className="mt-1 font-display font-black text-3xl tracking-tight" data-testid="credits-balance">
              {credits?.balance ?? "—"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {credits?.cost_per_project || 10} credits per video
            </div>
          </div>
          <Button
            size="lg"
            onClick={() => setShowNew(true)}
            disabled={!credits || credits.balance < (credits.cost_per_project || 10)}
            data-testid="new-project-btn"
          >
            <Plus className="h-4 w-4" /> New Project
          </Button>
        </div>
      </header>

      {credits && credits.balance < (credits.cost_per_project || 10) && (
        <div className="mb-6 rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            You're out of ViralCut credits. Buy a pack to keep creating clips.
            <div className="mt-3 flex flex-wrap gap-2">
              {credits.packs && Object.entries(credits.packs).map(([id, p]) => (
                <BuyPackButton key={id} id={id} pack={p} onDone={load} />
              ))}
            </div>
          </div>
        </div>
      )}

      {credits && credits.balance >= (credits.cost_per_project || 10) && credits.packs && (
        <div className="mb-6 rounded border border-border bg-card px-4 py-3">
          <div className="flex items-center flex-wrap gap-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
              <Coins className="h-3 w-3"/> Top up credits
            </div>
            {Object.entries(credits.packs).map(([id, p]) => (
              <BuyPackButton key={id} id={id} pack={p} onDone={load} />
            ))}
          </div>
        </div>
      )}

      <section>
        <h2 className="font-display font-bold text-xl tracking-tight mb-4">Your projects</h2>
        {loading ? (
          <div className="text-muted-foreground text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/> Loading…</div>
        ) : projects.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-border py-16 text-center" data-testid="studio-empty">
            <Film className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div className="mt-3 font-display font-bold text-lg">No projects yet</div>
            <div className="text-sm text-muted-foreground mt-1">Upload your first long video to see what ViralCut can pull out of it.</div>
            <Button className="mt-5" onClick={() => setShowNew(true)}>
              <Upload className="h-4 w-4" /> Upload video
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const chip = statusChip(p.status);
              return (
                <button
                  key={p.id}
                  onClick={() => nav(`/app/studio/${p.id}`)}
                  data-testid={`project-card-${p.id}`}
                  className="text-left rounded border border-border bg-card p-4 hover:border-primary/50 transition group"
                >
                  <div className="aspect-video rounded bg-secondary overflow-hidden mb-3 relative">
                    {p.thumbnail_path ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/video/thumb/${p.id}?auth=${encodeURIComponent(localStorage.getItem('contentos_token') || '')}&t=${p.updated_at}`}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e)=>{e.target.style.display='none';}}
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-muted-foreground/40">
                        <Film className="h-8 w-8" />
                      </div>
                    )}
                    {(p.status === "transcribing" || p.status === "finding_highlights" || p.status === "rendering") && (
                      <div className="absolute inset-0 bg-background/60 grid place-items-center">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{p.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {p.duration_sec ? `${(p.duration_sec/60).toFixed(1)} min · ` : ""}
                        {p.target_platform}
                      </div>
                    </div>
                    <div onClick={(e)=>{e.stopPropagation(); remove(p.id);}}
                      className="p-1.5 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground opacity-0 group-hover:opacity-100 transition"
                      data-testid={`project-delete-${p.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </div>
                  </div>
                  <div className={`mt-2 text-[11px] uppercase tracking-widest font-bold ${chip.color}`}>
                    {chip.text}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={(pid) => { setShowNew(false); nav(`/app/studio/${pid}`); }}
        />
      )}
    </div>
  );
}

function BuyPackButton({ id, pack, onDone }) {
  const [loading, setLoading] = useState(false);
  const buy = async () => {
    setLoading(true);
    try {
      // 1. Ask backend for the Paddle price ID + passthrough data
      const prep = await api.post("/api/billing/credits/checkout", { pack: id });
      const { price_id, customer_email, customer_name, passthrough } = prep.data;
      // 2. Open Paddle Checkout overlay
      const paddle = await getPaddle();
      if (!paddle) {
        toast.error("Paddle not configured. Contact support.");
        setLoading(false);
        return;
      }
      paddle.Checkout.open({
        items: [{ priceId: price_id, quantity: 1 }],
        customer: { email: customer_email, ...(customer_name ? { name: customer_name } : {}) },
        customData: passthrough,
        settings: {
          displayMode: "overlay",
          theme: "dark",
          successUrl: `${window.location.origin}/app/studio?credits_added=1`,
        },
        eventCallback: (evt) => {
          if (evt.name === "checkout.completed") {
            toast.success("Payment received. Credits will appear in a few seconds.");
            // Poll a few times because the webhook is async
            let tries = 0;
            const t = setInterval(async () => {
              tries++;
              await onDone?.();
              if (tries >= 6) clearInterval(t);
            }, 2000);
          }
          if (evt.name === "checkout.closed") {
            setLoading(false);
          }
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed to start");
      setLoading(false);
    }
  };

  // Not-configured packs show a disabled state
  if (pack.configured === false) {
    return (
      <Button size="sm" variant="secondary" disabled data-testid={`buy-pack-${id}-disabled`}>
        {pack.label}: {pack.credits} for ${pack.price_usd} · Coming soon
      </Button>
    );
  }
  return (
    <Button size="sm" variant="secondary" onClick={buy} disabled={loading} data-testid={`buy-pack-${id}`}>
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Coins className="h-3.5 w-3.5"/>}
      {pack.label}: {pack.credits} for ${pack.price_usd}
    </Button>
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("viral_short");
  const [platform, setPlatform] = useState("tiktok");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(""); // 'creating', 'uploading'
  const inputRef = useRef(null);

  const submit = async () => {
    if (!title.trim()) return toast.error("Give your project a title first");
    if (!file) return toast.error("Pick a video file");
    setCreating(true);
    try {
      setPhase("creating");
      const proj = await api.post("/api/video/projects", {
        title: title.trim(),
        content_goal: goal,
        target_platform: platform,
        description: description.trim() || null,
      });
      const pid = proj.data.id;

      setPhase("uploading");
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/api/video/projects/${pid}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 0,
      });
      toast.success("Uploaded. Processing starting…");
      onCreated(pid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setCreating(false);
      setPhase("");
      setProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-card border border-border rounded-lg p-6" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">New project</div>
            <h3 className="font-display font-black text-2xl tracking-tight mt-1">Upload your video</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary" data-testid="close-new-project">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Title</label>
            <input
              className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
              placeholder="e.g. Interview with Jane about AI marketing"
              value={title}
              onChange={(e)=>setTitle(e.target.value)}
              disabled={creating}
              data-testid="new-project-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Goal</label>
              <select
                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
                value={goal} onChange={(e)=>setGoal(e.target.value)} disabled={creating}
                data-testid="new-project-goal"
              >
                {GOAL_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Platform</label>
              <select
                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
                value={platform} onChange={(e)=>setPlatform(e.target.value)} disabled={creating}
                data-testid="new-project-platform"
              >
                {PLATFORM_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Description (optional)</label>
            <textarea
              className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm resize-none"
              rows={2}
              placeholder="What do you want ViralCut to prioritize?"
              value={description}
              onChange={(e)=>setDescription(e.target.value)}
              disabled={creating}
              data-testid="new-project-description"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Video file</label>
            <div
              onClick={()=>!creating && inputRef.current?.click()}
              className="mt-1 rounded border-2 border-dashed border-border bg-background p-6 text-center cursor-pointer hover:border-primary transition"
              data-testid="new-project-upload-zone"
            >
              <input
                ref={inputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
                className="hidden"
                onChange={(e)=>setFile(e.target.files?.[0] || null)}
                disabled={creating}
              />
              {file ? (
                <div className="text-sm">
                  <div className="font-semibold">{file.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{(file.size/1024/1024).toFixed(1)} MB</div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  <Upload className="h-5 w-5 mx-auto mb-2" />
                  Click to select MP4 / MOV / WebM (max 500 MB, 30 min)
                </div>
              )}
            </div>
          </div>

          {creating && (
            <div className="rounded border border-border bg-background p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold uppercase tracking-widest">
                  {phase === "creating" ? "Creating project…" : phase === "uploading" ? `Uploading ${progress}%` : "Working…"}
                </span>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary transition-all duration-200" style={{width: `${progress || 20}%`}} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={creating}>Cancel</Button>
          <Button onClick={submit} disabled={creating || !title.trim() || !file} data-testid="new-project-submit">
            <Sparkles className="h-4 w-4" /> Start processing (10 credits)
          </Button>
        </div>
      </div>
    </div>
  );
}
